import { Notification } from './notification';
import { NotificationMessageCode } from './notification-message-code';

/**
 * The translation key for a title that says what happened, with the stored title as `{name}`; null
 * for anything else, which keeps the title it was sent with.
 *
 * The stored title of a report notification is the report's name alone, the same for its start, its
 * result and its failure. The list and the toasts both use this, so they say the same thing.
 */
export function notificationTitleKey(notification: Notification | null | undefined): string | null {
  switch (notification?.messageCode) {
    case NotificationMessageCode.REPORT_GENERATION_STARTED: return 'notifications.title.reportStarted';
    case NotificationMessageCode.REPORT_GENERATION_FINISHED_SUCCESSFULLY: return 'notifications.title.reportReady';
    case NotificationMessageCode.REPORT_GENERATION_FAILED: return 'notifications.title.reportFailed';
    case NotificationMessageCode.SHARE_LINK_FIRST_ACCESSED: return 'notifications.title.shareLinkAccessed';
    default: return null;
  }
}

/** What a share-link notification says about the link that was opened. */
export interface ShareLinkAccess {
  shareLinkId?: number;
  name?: string;
  accessType?: string;
  accessedAt?: string;
}

/**
 * The opened link, or null for anything else. Notifications written before the payload carried the
 * link hold only its public id, as a string, and so have nothing to show.
 */
export function shareLinkAccess(notification: Notification | null | undefined): ShareLinkAccess | null {
  const data = notification?.data;
  if (notification?.messageCode !== NotificationMessageCode.SHARE_LINK_FIRST_ACCESSED || !data || typeof data !== 'object') {
    return null;
  }

  return {
    shareLinkId: data.shareLinkId,
    name: data.name || undefined,
    accessType: data.accessType || undefined,
    accessedAt: data.accessedAt || undefined,
  };
}
